import { Injectable } from '@angular/core';
import { from, Observable, throwError, zip } from 'rxjs';
import { IndexedDbDatabase } from './indexed-db-database.service';
import { catchError, delay, mapTo, retryWhen, scan, switchMap, take, tap } from 'rxjs/operators';
import { Dexie, IndexableType, Table } from 'dexie';
import { IndexedDbTable } from '../types';
import { DEFAULT_DEXIE_DB_RETRY } from '@constants/constants';
import { LoggerService } from '@services/logger.service';

@Injectable({ providedIn: 'root' })
export class IndexedDbService {
  constructor(private logger: LoggerService, private db: IndexedDbDatabase) {}

  public dropAll(): Observable<unknown> {
    return from(
      this.db
        .delete()
        .then(() => this.db.open())
        .then(() => {}),
    );
  }

  public clear(): Observable<unknown> {
    return from(this.db.delete());
  }

  public clearTables(...tables: IndexedDbTable[]): Observable<unknown> {
    return zip(
      ...this.getTables()
        .filter((table: Table<any, IndexableType>) => tables.includes(table.name))
        .map((table: Table<any, IndexableType>) => from(table.clear())),
    ).pipe(mapTo(null));
  }

  public addOne<T>(table: IndexedDbTable, item: T): Observable<IndexableType> {
    return from(this.db.table(table).add(item));
  }

  public addMultiple<T>(table: IndexedDbTable, items: T[]): Observable<IndexableType> {
    return from(this.db.table(table).bulkAdd(items));
  }

  public count(table: IndexedDbTable): Observable<number> {
    return from(this.db.table(table).count());
  }

  public addOrUpdateOne<T>(table: IndexedDbTable, item: T, key?: IndexableType): Observable<IndexableType> {
    if (key) {
      return from(this.db.table(table).put(item, key));
    }
    return from(this.db.table(table).put(item));
  }

  public addOrUpdateMultiple<T>(table: IndexedDbTable, items: T[], keys?: string[]): Observable<IndexableType | IndexableType[]> {
    if (keys) {
      return from(this.db.table(table).bulkPut(items, keys));
    }
    return from(this.db.table(table).bulkPut(items));
  }

  public deleteOne(table: IndexedDbTable, primaryKey: IndexableType): Observable<unknown> {
    return from(this.db.table(table).delete(primaryKey));
  }

  public deleteMultiple(table: IndexedDbTable, primaryKeys: IndexableType[]): Observable<unknown> {
    return from(this.db.table(table).bulkDelete(primaryKeys));
  }

  public clearAll(table: IndexedDbTable): Observable<unknown> {
    return from(this.db.table(table).clear());
  }

  public filter<T>(table: IndexedDbTable, filterFunction: (value: any) => boolean): Dexie.Collection<T, IndexableType> {
    return this.db.table(table).filter(filterFunction);
  }

  public offset<T>(table: IndexedDbTable, ignoreUpto: number): Dexie.Collection<T, IndexableType> {
    return this.db.table(table).offset(ignoreUpto);
  }

  public update<T>(table: IndexedDbTable, key: IndexableType, changes: Partial<T>): Observable<number> {
    return from(this.db.table(table).update(key, changes));
  }

  public getTables(): Dexie.Table<any, IndexableType>[] {
    return this.db.tables;
  }

  public toArray<T>(table: IndexedDbTable, callback?: (objects: any[]) => any): Observable<T[]> {
    if (callback) {
      return this.performDbOperation(() => this.db.table(table).toArray(callback), DEFAULT_DEXIE_DB_RETRY);
    }
    return this.performDbOperation(() => this.db.table(table).toArray(callback), DEFAULT_DEXIE_DB_RETRY);
  }

  private performDbOperation<T>(operation: () => Promise<T>, retryLimit: number = 3): Observable<T> {
    return from(operation()).pipe(
      catchError((error) => {
        this.logger.logException(error);
        if (error.name === 'DatabaseClosedError') {
          console.warn('Database was closed. Attempting to reopen...');
          return from(this.db.open()).pipe(
            switchMap(() => from(operation())),
            retryWhen((errors) =>
              errors.pipe(
                scan((retryCount, err) => {
                  if (retryCount >= retryLimit) {
                    console.error('Retry limit reached. Failing operation.');
                    throw err;
                  }
                  console.warn(`Retrying (${retryCount + 1}/${retryLimit})...`);
                  return retryCount + 1;
                }, 0),
                delay(1000),
              ),
            ),
          );
        }
        return throwError(error); // Rethrow other errors
      }),
    );
  }
}
